import cloneDeep from "lodash/cloneDeep";
import { ProseMirrorNode } from "../core/types";
import { createFullPresetSchema } from "../presets/schema-full-preset";
import { EditorState } from "prosemirror-state";
const hasAnyTextNodes = (content) => {
    return content.some((node) => node.type === "text");
};
export function removeEmptyTextNodes(content) {
    return content
        .map((node) => {
        if (node.type === "text" && node.text === "") {
            return null;
        }
        if (node.content) {
            return {
                ...node,
                content: removeEmptyTextNodes(node.content),
            };
        }
        return node;
    })
        .filter(Boolean);
}
const isTextNode = (node) => {
    return node.type === "text";
};
const isParagraphNode = (node) => {
    return node.type === "paragraph";
};
export function wrapTextsInParagraphs(content) {
    const wrappedNodes = [];
    let paragraphBuffer = [];
    for (const node of content) {
        if (isTextNode(node)) {
            paragraphBuffer.push(node);
        }
        else {
            if (paragraphBuffer.length > 0) {
                wrappedNodes.push({
                    type: "paragraph",
                    attrs: {
                        textAlign: "left",
                        size: "medium",
                    },
                    content: paragraphBuffer,
                });
                paragraphBuffer = [];
            }
            wrappedNodes.push(node);
        }
    }
    if (paragraphBuffer.length > 0) {
        wrappedNodes.push({
            type: "paragraph",
            attrs: {
                textAlign: null,
            },
            content: paragraphBuffer,
        });
    }
    return wrappedNodes;
}
const isSingleParagraphNode = (node) => {
    var _a;
    const content = (_a = node === null || node === void 0 ? void 0 : node.content) !== null && _a !== void 0 ? _a : [];
    return (isParagraphNode(node) && content.length === 1 && isParagraphNode(content[0]));
};
const unwrapNode = (node) => {
    if (isSingleParagraphNode(node)) {
        // Unwrap the nested paragraph by taking its content
        return unwrapNode(node.content[0]);
    }
    //  If it's not a paragraph, or not a single-nested paragraph, keep it as is
    return node;
};
export function unwrapNestedParagraphs(content) {
    var _a;
    const unwrappedNodes = [];
    // Iterate and recursively unwrap nested paragraphs
    for (const node of content) {
        //  If the node is a paragraph, only then check for unwrapping
        if (isParagraphNode(node)) {
            const content = (_a = node === null || node === void 0 ? void 0 : node.content) !== null && _a !== void 0 ? _a : [];
            if (content.length > 0 && !hasAnyTextNodes(content)) {
                unwrappedNodes.push(...unwrapNestedParagraphs(content));
            }
            else {
                unwrappedNodes.push(unwrapNode(node));
            }
        }
        else {
            unwrappedNodes.push(node);
        }
    }
    return unwrappedNodes;
}
export function removeContentIfEmptyParagraph(content) {
    return content
        .map((node) => {
        var _a;
        if (isParagraphNode(node)) {
            const nodeContent = (_a = node === null || node === void 0 ? void 0 : node.content) !== null && _a !== void 0 ? _a : [];
            if (nodeContent.length === 0) {
                return {
                    ...node,
                    content: undefined,
                };
            }
        }
        return node;
    })
        .filter(Boolean);
}
export function isDocStructureBroken(doc) {
    try {
        const schema = createFullPresetSchema();
        const node = nodeFromJSON(schema, doc);
        EditorState.create({ schema, doc: node });
        // It will only reach this line if the structure is not broken but the content is invalid with nested paragraphs
        return containsNestedParagraphs(doc.content);
    }
    catch (e) {
        return true;
    }
}
/** Check if document structure is invalid, it can be the case where it doesnt break the editor but its invalid nonetheless
 * The isDocStructureBroken only returns true when the structure would cause the editor to crash, this function also checks if
 * The data complies with the schema definition */
export function isDocStructureInvalid(doc) {
    try {
        const schema = createFullPresetSchema();
        const node = nodeFromJSON(schema, doc);
        node.check();
        if (containsNestedParagraphs(doc.content)) {
            return true;
        }
        return false;
    }
    catch (e) {
        return true;
    }
}
export function containsNestedParagraphs(content) {
    return content.some((node) => {
        var _a;
        if (isParagraphNode(node)) {
            const content = (_a = node === null || node === void 0 ? void 0 : node.content) !== null && _a !== void 0 ? _a : [];
            return content.some(isParagraphNode);
        }
        return false;
    });
}
export function nodeFromJSON(schema, json) {
    const node = ProseMirrorNode.fromJSON(schema, json);
    node.check();
    return node;
}
export function trimTextNodes(content) {
    return content.map((node) => {
        if (node.type === "text") {
            return {
                ...node,
                text: node.text.trim(),
            };
        }
        if (node.content) {
            return {
                ...node,
                content: trimTextNodes(node.content),
            };
        }
        return node;
    });
}
/**
 * Removes empty paragraphs from the start and end of the document.
 */
export function trimEmptyParagraphs(content) {
    var _a, _b;
    const pNodes = ["paragraph_small", "paragraph"];
    if (!(content === null || content === void 0 ? void 0 : content.length)) {
        return content;
    }
    const firstNode = content.at(0);
    const lastNode = content.at(-1);
    if (pNodes.includes(firstNode.type) && !((_a = firstNode.content) === null || _a === void 0 ? void 0 : _a.length)) {
        return trimEmptyParagraphs(content.slice(1));
    }
    if (pNodes.includes(lastNode.type) && !((_b = lastNode.content) === null || _b === void 0 ? void 0 : _b.length)) {
        return trimEmptyParagraphs(content.slice(0, -1));
    }
    return content;
}
export function normalizeDoc(doc) {
    const steps = [
        removeEmptyTextNodes,
        wrapTextsInParagraphs,
        unwrapNestedParagraphs,
        removeContentIfEmptyParagraph,
        trimEmptyParagraphs,
        moveHardBreaksInsideContainers,
    ];
    const content = doc.content;
    const normalizedContent = steps.reduce((content, step) => step(content), content);
    return {
        ...doc,
        content: normalizedContent,
    };
}
export function trimText(content) {
    const result = cloneDeep(content);
    const textNodes = findNodesByType(result, "text");
    const first = textNodes.at(0);
    const last = textNodes.at(-1);
    if (first) {
        first.text = first.text.trimStart();
    }
    if (last) {
        last.text = last.text.trimEnd();
    }
    return result;
}
function findNodesByType(content, type) {
    const nodes = [];
    for (const node of content) {
        if (node.type === type) {
            nodes.push(node);
        }
        if (node.content) {
            nodes.push(...findNodesByType(node.content, type));
        }
    }
    return nodes;
}
function moveHardBreaksInsideContainers(content) {
    if (content.length === 0) {
        return content;
    }
    const result = [];
    for (let i = 0; i < content.length; i++) {
        const currentNode = content[i];
        if (currentNode.type === "hard_break" && i > 0) {
            const previousNode = result[result.length - 1];
            if (previousNode === null || previousNode === void 0 ? void 0 : previousNode.content) {
                previousNode.content.push(currentNode);
            }
        }
        else {
            const processedContent = currentNode.content
                ? moveHardBreaksInsideContainers(currentNode.content)
                : currentNode.content;
            result.push({ ...currentNode, content: processedContent });
        }
    }
    return result;
}
